import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '@/api/client';
import type {
  ClaimArchitecture, ClaimArchitectureResponse,
  ArchEssentialElement, ArchOptionalElement, ArchLadder,
} from '@/api/client';

import { apiErrorText } from '../../lib/describeApiError';
/**
 * Stage-3 "Claim Architecture" review panel — docs/CLAIM_ARCHITECTURE_PLAN.md
 * Phase 4.
 *
 * The pre-pass decides which elements are essential, how far each term
 * is abstracted, and whether the field of use belongs in claim 1. Those
 * are strategic judgments, and §11.2 of the plan is the design brief:
 * attorneys tolerate automation, but not *unexplained* judgment in
 * their own professional domain. So this panel leads with the
 * reasoning — `why_essential`, `why_not_essential`, each ladder's `why`
 * and the `support_species` a rung rests on — and only then offers the
 * toggles.
 *
 * Saving writes `source='edited'`, which the backend honours over any
 * later re-derive, so an override survives regenerates until the
 * drafter explicitly rebuilds.
 */

interface Props {
  matterId: string;
  /** Claims already exist, so an edit implies a regenerate to take effect. */
  hasClaims?: boolean;
  onRequestRegenerate?: () => void;
}

const card = 'border border-zinc-200 rounded-md bg-white';
const btn =
  'inline-flex h-8 items-center justify-center rounded border border-input ' +
  'bg-background px-2.5 text-xs font-medium hover:bg-accent disabled:opacity-50';
const primaryBtn =
  'inline-flex h-8 items-center justify-center rounded-md bg-primary px-3 ' +
  'text-xs font-medium text-primary-foreground hover:bg-primary/90 ' +
  'disabled:opacity-50';

function Why({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return (
    <div className="mt-1 text-[11px] leading-relaxed text-zinc-600">
      {children}
    </div>
  );
}

function deepClone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

export default function ClaimArchitecturePanel({
  matterId, hasClaims, onRequestRegenerate,
}: Props) {
  const [state, setState] = useState<ClaimArchitectureResponse | null>(null);
  const [draft, setDraft] = useState<ClaimArchitecture | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await api.getClaimArchitecture(matterId);
      setState(res);
      setDraft(res.artifact ? deepClone(res.artifact) : null);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to load claim architecture.'));
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    if (!state?.artifact || !draft) return false;
    return JSON.stringify(state.artifact) !== JSON.stringify(draft);
  }, [state, draft]);

  // ── Overrides ────────────────────────────────────────────────────
  // Demote/promote move an element between the two lists. The element's
  // ladder entry is deliberately left untouched: a demoted element may
  // well come back, and discarding the rung analysis would force the
  // drafter to redo a judgment the pre-pass already reasoned about.

  const demote = (el: ArchEssentialElement) => {
    if (!draft) return;
    const next = deepClone(draft);
    next.essential_elements = next.essential_elements.filter(
      e => e.element_id !== el.element_id,
    );
    next.optional_elements = [
      ...next.optional_elements,
      {
        element_id: el.element_id,
        claim_term: el.claim_term,
        why_not_essential: 'Demoted by the drafter during architecture review.',
        target: 'dependent',
      },
    ];
    setDraft(next);
  };

  const promote = (el: ArchOptionalElement) => {
    if (!draft) return;
    const next = deepClone(draft);
    next.optional_elements = next.optional_elements.filter(
      e => e.element_id !== el.element_id,
    );
    next.essential_elements = [
      ...next.essential_elements,
      {
        element_id: el.element_id,
        claim_term: el.claim_term,
        as_disclosed: '',
        why_essential: 'Marked essential by the drafter during architecture review.',
        actor_for_functions: [],
        evidence: '',
      },
    ];
    setDraft(next);
  };

  const chooseRung = (elementId: string, idx: number) => {
    if (!draft) return;
    const next = deepClone(draft);
    next.abstraction_ladder = next.abstraction_ladder.map(l =>
      l.element_id === elementId
        ? { ...l, chosen_index: idx, chosen_rung: l.rungs[idx] ?? l.chosen_rung }
        : l,
    );
    // Keep the essential element's claim_term in step with the rung —
    // the drafting prompt reads claim_term, so a rung change that left
    // it stale would be a no-op the drafter could not see.
    const rung = next.abstraction_ladder.find(l => l.element_id === elementId);
    if (rung) {
      next.essential_elements = next.essential_elements.map(e =>
        e.element_id === elementId ? { ...e, claim_term: rung.chosen_rung } : e,
      );
    }
    setDraft(next);
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      const res = await api.saveClaimArchitecture(matterId, draft);
      setState(s => (s ? { ...s, artifact: res.artifact, source: 'edited', is_stale: false } : s));
      setDraft(deepClone(res.artifact));
      setOk(
        hasClaims
          ? 'Saved. Regenerate the claim set for these changes to take effect.'
          : 'Saved. The next generate will draft against these decisions.',
      );
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setErr(
        typeof d === 'string' ? d
          : d?.message
            ? `${d.message}${d.errors?.length ? ` (${d.errors.join('; ')})` : ''}`
            : 'Failed to save.',
      );
    } finally {
      setBusy(false);
    }
  };

  const rebuild = async () => {
    if (!window.confirm(
      'Rebuild discards your overrides and re-derives the architecture from ' +
      'the current Stage 2 / 2.5 inputs. Continue?',
    )) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      const res = await api.rebuildClaimArchitecture(matterId);
      setState(s => (s ? { ...s, artifact: res.artifact, source: 'auto', is_stale: false } : s));
      setDraft(deepClone(res.artifact));
      setOk('Rebuilt from the current Stage 2 / 2.5 inputs.');
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : d?.message || 'Rebuild failed.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className={`${card} p-3 text-xs text-zinc-500`}>
        Loading claim architecture…
      </div>
    );
  }
  // Feature flag off, or the pre-pass has not run for this matter yet —
  // stay silent rather than showing an empty shell.
  if (!state || !state.enabled || !state.present || !draft) return null;

  const ladderFor = (id: string): ArchLadder | undefined =>
    draft.abstraction_ladder.find(l => l.element_id === id);

  const unsupported = (draft.factual_assertions || []).filter(a => !a.supported);

  return (
    <div className={card} data-testid="claim-architecture-panel">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-3 text-left"
      >
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-zinc-900">
            Claim Architecture
          </span>
          <span className="text-[11px] text-zinc-500">
            {draft.essential_elements.length} essential ·{' '}
            {draft.optional_elements.length} optional
          </span>
          {state.source === 'edited' && (
            <span className="text-[10px] rounded px-1.5 py-0.5 bg-violet-100 text-violet-800">
              edited
            </span>
          )}
          {state.is_stale && (
            <span className="text-[10px] rounded px-1.5 py-0.5 bg-amber-100 text-amber-800">
              Stage 2 changed
            </span>
          )}
          {unsupported.length > 0 && (
            <span className="text-[10px] rounded px-1.5 py-0.5 bg-red-100 text-red-800">
              {unsupported.length} unsupported assertion
              {unsupported.length === 1 ? '' : 's'}
            </span>
          )}
        </span>
        <span className="text-zinc-400 text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="border-t border-zinc-100 p-3 space-y-4">
          {draft.inventive_core && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-zinc-500">
                Inventive core
              </div>
              <div className="text-sm text-zinc-800">{draft.inventive_core}</div>
            </div>
          )}

          {/* Field of use — the P1 "UAV flight controller in claim 1" trap. */}
          {draft.field_of_use && (draft.field_of_use.disclosed || draft.field_of_use.claim1_preamble) && (
            <div className="rounded border border-zinc-100 bg-zinc-50 p-2">
              <div className="text-[11px] uppercase tracking-wide text-zinc-500">
                Field of use
              </div>
              <div className="text-xs text-zinc-800">
                Disclosed as <em>{draft.field_of_use.disclosed || '—'}</em>;{' '}
                {draft.field_of_use.required_by_novelty
                  ? 'novelty depends on it, so it stays in claim 1.'
                  : 'novelty does not depend on it, so claim 1 is drafted at the general level.'}
              </div>
              {draft.field_of_use.claim1_preamble && (
                <div className="mt-1 text-xs text-zinc-600">
                  Claim 1 preamble:{' '}
                  <span className="font-medium text-zinc-800">
                    {draft.field_of_use.claim1_preamble}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Essential elements + their ladders. */}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1">
              Essential in claim 1
            </div>
            <div className="space-y-2">
              {draft.essential_elements.map(el => {
                const lad = ladderFor(el.element_id);
                return (
                  <div
                    key={el.element_id}
                    className="rounded border border-zinc-100 p-2"
                    data-testid={`arch-essential-${el.element_id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-900">
                          {el.claim_term || el.element_id}
                        </div>
                        {el.as_disclosed && (
                          <div className="text-[11px] text-zinc-500">
                            disclosed as “{el.as_disclosed}”
                          </div>
                        )}
                        <Why>{el.why_essential}</Why>
                        {el.evidence && (
                          <div className="mt-0.5 text-[11px] text-zinc-400">
                            {el.evidence}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className={btn}
                        onClick={() => demote(el)}
                        title="Move to the dependent ladder"
                      >
                        Demote
                      </button>
                    </div>

                    {lad && lad.rungs.length > 0 && (
                      <div className="mt-2">
                        <div className="text-[11px] text-zinc-500">
                          Abstraction ladder — broadest first
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {lad.rungs.map((r, i) => (
                            <button
                              key={`${lad.element_id}-${i}`}
                              type="button"
                              onClick={() => chooseRung(lad.element_id, i)}
                              className={
                                'rounded px-2 py-0.5 text-[11px] border ' +
                                (i === lad.chosen_index
                                  ? 'border-emerald-300 bg-emerald-50 text-emerald-900 font-medium'
                                  : 'border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50')
                              }
                              title={
                                i === lad.chosen_index
                                  ? 'Chosen rung'
                                  : 'Use this rung in claim 1'
                              }
                            >
                              {r}
                            </button>
                          ))}
                        </div>
                        <Why>{lad.why}</Why>
                        {lad.support_species.length > 0 && (
                          <div className="mt-1 text-[11px] text-zinc-500">
                            Supported by:{' '}
                            {lad.support_species.join('; ')}
                          </div>
                        )}
                        {lad.novelty_carrier && (
                          <div className="mt-1 text-[11px] text-amber-700">
                            Carries the novelty — broadening this rung
                            weakens the distinguishing feature.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {draft.essential_elements.length === 0 && (
                <div className="text-xs text-zinc-500">
                  No essential elements recorded.
                </div>
              )}
            </div>
          </div>

          {/* Optional elements — the P2 "valve architecture in claim 1" trap. */}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1">
              Kept out of claim 1
            </div>
            <div className="space-y-2">
              {draft.optional_elements.map(el => (
                <div
                  key={el.element_id}
                  className="rounded border border-zinc-100 p-2 flex items-start justify-between gap-2"
                  data-testid={`arch-optional-${el.element_id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-900">
                      {el.claim_term || el.element_id}
                      <span className="ml-2 text-[10px] rounded px-1.5 py-0.5 bg-zinc-100 text-zinc-600">
                        {el.target}
                      </span>
                    </div>
                    <Why>{el.why_not_essential}</Why>
                  </div>
                  <button
                    type="button"
                    className={btn}
                    onClick={() => promote(el)}
                    title="Require this element in claim 1"
                  >
                    Mark essential
                  </button>
                </div>
              ))}
              {draft.optional_elements.length === 0 && (
                <div className="text-xs text-zinc-500">
                  Nothing deferred out of claim 1.
                </div>
              )}
            </div>
          </div>

          {/* Independent aspects — the missed retrofit-apparatus claim. */}
          {(draft.independent_claim_plan || []).length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1">
                Planned independent claims
              </div>
              <ul className="space-y-1">
                {(draft.independent_claim_plan || []).map((p, i) => (
                  <li key={i} className="text-xs text-zinc-800">
                    <span className="font-medium">{p.aspect}</span>
                    <span className="text-zinc-500"> ({p.category})</span>
                    <Why>{p.rationale}</Why>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Unsupported assertions — pattern 4. */}
          {unsupported.length > 0 && (
            <div className="rounded border border-red-200 bg-red-50 p-2">
              <div className="text-[11px] uppercase tracking-wide text-red-700 mb-1">
                Assertions the disclosure does not support
              </div>
              <ul className="space-y-1">
                {unsupported.map((a, i) => (
                  <li key={i} className="text-xs text-red-900">
                    “{a.assertion}”
                    {a.evidence && (
                      <div className="text-[11px] text-red-700">{a.evidence}</div>
                    )}
                    {a.recast && (
                      <div className="text-[11px] text-emerald-800">
                        Recast: {a.recast}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {err && <div className="text-xs text-red-700">{err}</div>}
          {ok && <div className="text-xs text-emerald-700">{ok}</div>}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              className={primaryBtn}
              disabled={!dirty || busy}
              onClick={save}
              data-testid="arch-save"
            >
              {busy ? 'Saving…' : 'Save overrides'}
            </button>
            {dirty && (
              <button
                type="button"
                className={btn}
                disabled={busy}
                onClick={() => setDraft(deepClone(state.artifact!))}
              >
                Discard changes
              </button>
            )}
            <button
              type="button"
              className={btn}
              disabled={busy}
              onClick={rebuild}
              title="Discard overrides and re-derive from Stage 2 / 2.5"
            >
              Rebuild
            </button>
            {hasClaims && state.source === 'edited' && !dirty && onRequestRegenerate && (
              <button
                type="button"
                className={btn}
                disabled={busy}
                onClick={onRequestRegenerate}
              >
                Regenerate claims
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
